import React, { useEffect, useMemo } from "react";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import "./WelocateCompareSideBySide.css";

const ENERGY_LABEL_KEYS = [
  "Minimaal Energie Label",
  "Minimaal Energielabel",
  "Minimum Energy Label",
];
const BEDROOM_KEYS = ["Bedrooms", "Slaapkamers"];
const BATHROOM_KEYS = ["Bathrooms", "Badkamers"];
const BUILD_YEAR_KEYS = ["Bouwjaar", "Building Year"];
const PLOT_AREA_KEYS = [
  "Perceeloppervlakte",
  "Plot Area",
  "Buitenruimte",
  "Outdoor Space",
];
const TYPE_KEYS = ["Type", "Woningtype", "Soort woning", "Property type"];

const STATUS_LABELS = {
  for_sale: "Available",
  available: "Available",
  under_bid: "Under bid",
  under_option: "Under option",
  sold: "Sold",
  sold_under_reservation: "Sold (Reservation)",
};

const STATUS_COLORS = {
  for_sale: "#2f4b9b",
  available: "#2f4b9b",
  under_bid: "#E27C00",
  under_option: "#E27C00",
  sold: "#9CA3AF",
  sold_under_reservation: "#9CA3AF",
};

const ENERGY_RANK = {
  "A+++": 0, "A++": 1, "A+": 2,
  A: 3, B: 4, C: 5, D: 6, E: 7, F: 8, G: 9,
};

const ENERGY_COLOR = {
  "A+++": "#1f9d55", "A++": "#1f9d55", "A+": "#1f9d55",
  A: "#1f9d55", B: "#7cb342", C: "#c98a00",
  D: "#e8a400", E: "#e07a00", F: "#e75232", G: "#d32f2f",
};

const findAttr = (listing, keys) => {
  const attrs = listing?.attributes || [];
  const found = attrs.find((a) => keys.includes(a.key));
  return found?.value ?? null;
};

const parseNumeric = (val) => {
  if (val == null) return null;
  if (typeof val === "number") return val;
  const cleaned = String(val)
    .replace(/[^\d.,-]/g, "")
    .replace(/\./g, "")
    .replace(",", ".");
  const n = parseFloat(cleaned);
  return Number.isFinite(n) ? n : null;
};

const formatEuro = (p) =>
  p == null ? null : `€${Math.round(p).toLocaleString("nl-NL")}`;

const getFirstPhoto = (listing) => {
  const files = listing?.files || [];
  const first = files.find((f) => f?.t_realmex_data_firehose_file_path);
  return first?.t_realmex_data_firehose_file_path || null;
};

const bestIndices = (values, mode) => {
  const nums = values.map((v) =>
    typeof v === "number" && Number.isFinite(v) ? v : null
  );
  const present = nums.filter((v) => v != null);
  if (present.length < 2) return new Set();
  const min = Math.min(...present);
  const max = Math.max(...present);
  if (min === max) return new Set();
  const bestVal = mode === "min" ? min : max;
  const out = new Set();
  nums.forEach((v, i) => {
    if (v === bestVal) out.add(i);
  });
  return out;
};

const bestEnergyIndices = (labels) => {
  const ranks = labels.map((l) =>
    l ? ENERGY_RANK[String(l).toUpperCase()] ?? null : null
  );
  const present = ranks.filter((r) => r != null);
  if (present.length < 2) return new Set();
  const min = Math.min(...present);
  const max = Math.max(...present);
  if (min === max) return new Set();
  const out = new Set();
  ranks.forEach((r, i) => {
    if (r === min) out.add(i);
  });
  return out;
};

const WelocateCompareSideBySide = ({ open, compareList, onClose, onRemove }) => {
  useEffect(() => {
    if (open && compareList && compareList.length < 2) {
      onClose && onClose();
    }
  }, [open, compareList, onClose]);

  const metrics = useMemo(() => {
    return (compareList || []).map((listing) => {
      const price = listing?.price ?? null;
      const area = parseNumeric(listing?.area);
      const pricePerM2 =
        price != null && area ? Math.round(price / area) : null;
      const bedrooms = parseNumeric(findAttr(listing, BEDROOM_KEYS));
      const bathrooms = parseNumeric(findAttr(listing, BATHROOM_KEYS));
      const buildYear = parseNumeric(findAttr(listing, BUILD_YEAR_KEYS));
      const plotArea = parseNumeric(findAttr(listing, PLOT_AREA_KEYS));
      const energyLabel = findAttr(listing, ENERGY_LABEL_KEYS);
      const type = findAttr(listing, TYPE_KEYS);
      const status = listing?.sold_status ?? null;
      return {
        price, area, pricePerM2, bedrooms, bathrooms,
        buildYear, plotArea, energyLabel, type, status,
      };
    });
  }, [compareList]);

  const bestPrice = useMemo(
    () => bestIndices(metrics.map((m) => m.price), "min"),
    [metrics]
  );
  const bestPricePerM2 = useMemo(
    () => bestIndices(metrics.map((m) => m.pricePerM2), "min"),
    [metrics]
  );
  const bestArea = useMemo(
    () => bestIndices(metrics.map((m) => m.area), "max"),
    [metrics]
  );
  const bestBedrooms = useMemo(
    () => bestIndices(metrics.map((m) => m.bedrooms), "max"),
    [metrics]
  );
  const bestBuildYear = useMemo(
    () => bestIndices(metrics.map((m) => m.buildYear), "max"),
    [metrics]
  );
  const bestEnergy = useMemo(
    () => bestEnergyIndices(metrics.map((m) => m.energyLabel)),
    [metrics]
  );

  if (!open || !compareList || compareList.length < 2) return null;

  const N = compareList.length;
  const gridStyle = { "--compare-cols": N };

  const renderStatus = (m) => {
    if (!m.status) return "—";
    const color = STATUS_COLORS[m.status] || "#2f4b9b";
    return (
      <span
        className="wl-sbs-status-pill"
        style={{ background: `${color}14`, color }}
      >
        <span className="wl-sbs-status-dot" style={{ background: color }} />
        {STATUS_LABELS[m.status] ?? m.status}
      </span>
    );
  };

  const renderEnergy = (m) => {
    if (!m.energyLabel) return "—";
    const color =
      ENERGY_COLOR[String(m.energyLabel).toUpperCase()] || "#7a8093";
    return (
      <span
        className="wl-sbs-energy-pill"
        style={{ color, borderColor: color }}
      >
        Label {m.energyLabel}
      </span>
    );
  };

  const rows = [
    { key: "status", label: "Status", render: renderStatus },
    {
      key: "price",
      label: (
        <>
          <span className="wl-sbs-label-desktop">Asking price</span>
          <span className="wl-sbs-label-mobile">Price</span>
        </>
      ),
      best: bestPrice,
      render: (m) => formatEuro(m.price) ?? "—",
    },
    {
      key: "price_m2",
      label: (
        <>
          <span className="wl-sbs-label-desktop">Price per m²</span>
          <span className="wl-sbs-label-mobile">Price / m²</span>
        </>
      ),
      best: bestPricePerM2,
      render: (m) =>
        m.pricePerM2 != null ? (
          <>
            {formatEuro(m.pricePerM2)}{" "}
            <span className="wl-sbs-unit">/m²</span>
          </>
        ) : (
          "—"
        ),
    },
    {
      key: "area",
      label: "Living area",
      best: bestArea,
      render: (m) =>
        m.area != null ? (
          <>
            {m.area} <span className="wl-sbs-unit">m²</span>
          </>
        ) : (
          "—"
        ),
    },
    {
      key: "bedrooms",
      label: "Bedrooms",
      best: bestBedrooms,
      render: (m) => (m.bedrooms != null ? m.bedrooms : "—"),
    },
    {
      key: "bathrooms",
      label: "Bathrooms",
      render: (m) => (m.bathrooms != null ? m.bathrooms : "—"),
    },
    { key: "type", label: "Type", render: (m) => m.type ?? "—" },
    {
      key: "year",
      label: "Year built",
      best: bestBuildYear,
      render: (m) => (m.buildYear != null ? m.buildYear : "—"),
    },
    {
      key: "energy",
      label: (
        <>
          <span className="wl-sbs-label-desktop">Energy label</span>
          <span className="wl-sbs-label-mobile">E. label</span>
        </>
      ),
      best: bestEnergy,
      render: renderEnergy,
    },
  ];

  return (
    <div className="wl-sbs-overlay" onClick={onClose}>
      <div
        className="wl-sbs-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label="Compare listings"
      >
        <div className="wl-sbs-header">
          <div>
            <h2 className="wl-sbs-title">Compare listings</h2>
            <p className="wl-sbs-subtitle">
              Best value for each measure is marked
              <span className="wl-sbs-subtitle-chip" aria-hidden="true">
                <CheckIcon style={{ fontSize: 12 }} />
              </span>
            </p>
          </div>
          <button
            type="button"
            className="wl-sbs-close"
            aria-label="Close"
            onClick={onClose}
          >
            <CloseIcon style={{ fontSize: 20 }} />
          </button>
        </div>

        <div className="wl-sbs-body">
          <div className="wl-sbs-grid" style={gridStyle}>
            <div className="wl-sbs-cell wl-sbs-cell--corner" />
            {compareList.map((listing, i) => {
              const energy = findAttr(listing, ENERGY_LABEL_KEYS);
              const photo = getFirstPhoto(listing);
              const subtitle = [listing?.postal, listing?.city]
                .filter(Boolean)
                .join(" · ");
              return (
                <div
                  className="wl-sbs-cell wl-sbs-property-cell"
                  key={`prop-${i}`}
                >
                  <div
                    className={`wl-sbs-property-card ${
                      photo ? "wl-sbs-property-card--photo" : ""
                    }`}
                    style={
                      photo ? { backgroundImage: `url(${photo})` } : undefined
                    }
                  >
                    {photo && (
                      <div
                        className="wl-sbs-property-overlay"
                        aria-hidden="true"
                      />
                    )}
                    {photo && (
                      <img
                        src={photo}
                        alt=""
                        aria-hidden="true"
                        className="wl-sbs-property-avatar-img"
                      />
                    )}
                    <button
                      type="button"
                      className="wl-sbs-property-remove"
                      aria-label="Remove"
                      onClick={() => onRemove && onRemove(listing)}
                    >
                      <CloseIcon style={{ fontSize: 16 }} />
                    </button>
                    {energy && (
                      <span className="wl-sbs-property-energy">{energy}</span>
                    )}
                    <div className="wl-sbs-property-content">
                      <div className="wl-sbs-property-title">
                        {listing?.title || "—"}
                      </div>
                      {subtitle && (
                        <div className="wl-sbs-property-sub">{subtitle}</div>
                      )}
                      {listing?.city && (
                        <div className="wl-sbs-property-city">
                          {listing.city}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}

            {rows.map((row, rowIndex) => {
              const isAlt = rowIndex % 2 === 0;
              return (
                <React.Fragment key={row.key}>
                  <div
                    className={`wl-sbs-cell wl-sbs-cell--label ${
                      isAlt ? "wl-sbs-cell--alt" : ""
                    }`}
                  >
                    {row.label}
                  </div>
                  {metrics.map((m, i) => {
                    const isBest = row.best?.has?.(i) ?? false;
                    return (
                      <div
                        key={`${row.key}-${i}`}
                        className={`wl-sbs-cell wl-sbs-cell--value ${
                          isAlt ? "wl-sbs-cell--alt" : ""
                        } ${isBest ? "wl-sbs-cell--best" : ""}`}
                      >
                        <span className="wl-sbs-value">{row.render(m)}</span>
                        {isBest && (
                          <span className="wl-sbs-best-pill">
                            <CheckIcon style={{ fontSize: 10 }} />
                            BEST
                          </span>
                        )}
                      </div>
                    );
                  })}
                </React.Fragment>
              );
            })}

            <div className="wl-sbs-cell wl-sbs-cell--action-label" />
            {compareList.map((listing, i) => (
              <div
                key={`action-${i}`}
                className="wl-sbs-cell wl-sbs-cell--action"
              >
                {listing?.public_url ? (
                  <a
                    href={listing.public_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="wl-sbs-view-link"
                  >
                    <span className="wl-sbs-view-link-text">
                      <span className="wl-sbs-label-desktop">View listing</span>
                      <span className="wl-sbs-label-mobile">View</span>
                    </span>
                    <OpenInNewIcon
                      className="wl-sbs-view-link-icon"
                      style={{ fontSize: 15 }}
                    />
                  </a>
                ) : (
                  <span className="wl-sbs-view-link wl-sbs-view-link--disabled">
                    <span className="wl-sbs-view-link-text">Unavailable</span>
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default WelocateCompareSideBySide;
